
import React, { useState } from "react";
import { EnhancedSlider } from "@/components/ui/enhanced-slider";
import { cn } from "@/lib/utils";
import { useThemeMode } from "@/hooks/useThemeMode";
import { useThemeContext, getGlowClasses } from "@/hooks/useThemeContext";
import { motion } from "framer-motion";

interface SliderDisplayProps {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  showMinMax?: boolean;
  formatValue?: (value: number) => string;
  onChange: (value: number[]) => void;
  className?: string;
}

const SliderDisplay: React.FC<SliderDisplayProps> = ({
  value,
  min,
  max,
  step,
  disabled = false,
  showMinMax = true,
  formatValue,
  onChange,
  className,
}) => {
  const { isDark } = useThemeMode();
  const { useGlow } = useThemeContext();
  const [isHovering, setIsHovering] = useState(false);
  
  // Format values for min and max display
  const formattedMin = formatValue ? formatValue(min) : min.toString();
  const formattedMax = formatValue ? formatValue(max) : max.toString();
  
  // Calculate percentage for background fill
  const percentage = ((value - min) / (max - min)) * 100;
  
  return (
    <div 
      className={cn("space-y-2", className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="relative pt-5">
        {/* Floating value bubble */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ 
            opacity: isHovering ? 1 : 0, 
            y: isHovering ? 0 : 10,
            left: `calc(${percentage}% - 20px)`
          }}
          transition={{ duration: 0.2 }}
          className={cn(
            "absolute -top-1 px-2 py-1 rounded-lg text-xs font-medium",
            isDark 
              ? "bg-primary/20 text-primary-foreground backdrop-blur-sm" 
              : "bg-primary/10 text-primary backdrop-blur-sm",
            useGlow ? getGlowClasses(true, "primary") : ""
          )}
        >
          {formatValue ? formatValue(value) : value}
        </motion.div>
        
        <EnhancedSlider
          value={[value]}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onValueChange={onChange}
          className="mt-2 h-2"
        />
      </div>
      
      {showMinMax && (
        <div className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
          <motion.span 
            className="transition-all duration-300 hover:text-primary"
            whileHover={{ scale: 1.05 }}
          >
            {formattedMin}
          </motion.span>
          <motion.span 
            className="transition-all duration-300 hover:text-primary"
            whileHover={{ scale: 1.05 }}
          >
            {formattedMax}
          </motion.span>
        </div>
      )}
    </div>
  );
};

export default SliderDisplay;
